import { Text, TextButton } from '@wix/design-system';
import {
  EllipsisForTrans,
  useDomainIntent,
} from '@wix/package-picker-shared-eclipse';
import { FeaturesGrid as PlatformFeaturesGrid } from '@wix/premium-purchase-platform/@components';
import {
  CreativeOffer,
  PurchaseOffering,
} from '@wix/premium-purchase-platform/@sdk';
import classNames from 'classnames';
import React from 'react';
import { useJapanesePremiumExperience } from '../../hooks/useJapanesePremiumExperience';
import { EclipsePackagePickerModule } from '../../modules/eclipse-package-picker.module';
import styles from './FeaturesGrid.scss';
import { FeaturesGridFeatureRowCell } from './FeaturesGridFeatureRowCell';
import { FeaturesGridNewBadge } from './FeaturesGridNewBadge';

interface FeaturesGridFeatureRowProps {
  feature: CreativeOffer.GridFeature;
  products?: PurchaseOffering.PurchaseProduct[];
}

export const FeaturesGridFeatureRow = ({
  feature,
  products,
}: FeaturesGridFeatureRowProps) => {
  const { t, Trans } = EclipsePackagePickerModule.internals.useTranslations();
  const isJapanesePremiumExperience = useJapanesePremiumExperience();
  const { showDomain, domainName, getFreeDomainTooltipKey } = useDomainIntent();
  const freeDomainTooltipKey = getFreeDomainTooltipKey(
    feature.content?.name?.translationKey,
  );

  if (!feature.content?.name?.translationKey || feature.metadata?.hidden) {
    return null;
  }

  const translationValues = { domainName: showDomain ? domainName : undefined };
  const translationTags = {
    ellipsis: <EllipsisForTrans maxWidth={160} />,
    url: (
      <TextButton
        as="a"
        target="_blank"
        underline="onHover"
        size={isJapanesePremiumExperience ? 'tiny' : 'small'}
        href={t(feature.metadata?.tooltipUrlKey ?? '')}
        onClick={() => {
          EclipsePackagePickerModule.internals.bi.onFeatureTooltipLinkClick?.({
            featureKey: feature.content?.name?.translationKey,
            url: t(feature.metadata?.tooltipUrlKey ?? ''),
          });
        }}
      />
    ),
  };

  const tooltipKey =
    freeDomainTooltipKey ?? feature.content?.tooltip?.translationKey ?? '';

  return (
    <PlatformFeaturesGrid.Content.FeatureRow.Root
      size={isJapanesePremiumExperience ? 'medium' : 'small'}
    >
      <PlatformFeaturesGrid.Content.FeatureRow.Cell
        className={classNames(styles.featureRowCell, {
          [styles.jpFeatureRowCell]: isJapanesePremiumExperience,
        })}
        overflow={isJapanesePremiumExperience ? 'visible' : undefined}
        verticalAlign={isJapanesePremiumExperience ? 'top' : undefined}
        paddingTop={isJapanesePremiumExperience ? 'SP1' : undefined}
        paddingBottom={isJapanesePremiumExperience ? 'SP1' : undefined}
      >
        <PlatformFeaturesGrid.Content.FeatureRow.Header
          nameWeight={isJapanesePremiumExperience ? 'bold' : 'normal'}
          descriptionAsSubtitle={isJapanesePremiumExperience}
          isDescriptionSecondary={isJapanesePremiumExperience}
          name={
            <Trans
              i18nKey={feature.content?.name?.translationKey ?? ''}
              values={translationValues}
              components={translationTags}
            />
          }
          description={
            isJapanesePremiumExperience
              ? undefined
              : feature.content?.description?.translationKey
              ? t(feature.content?.description?.translationKey)
              : undefined
          }
          tooltip={
            isJapanesePremiumExperience ? (
              <Text size="tiny" weight="thin" secondary skin="standard">
                <Trans
                  i18nKey={tooltipKey}
                  values={translationValues}
                  components={translationTags}
                />
              </Text>
            ) : (
              <Text size="small" skin="standard">
                <Trans
                  i18nKey={tooltipKey}
                  values={translationValues}
                  components={translationTags}
                />
              </Text>
            )
          }
          badge={
            feature.metadata?.isNew === 'true' ? (
              <FeaturesGridNewBadge dataHook="features-grid-new-badge" />
            ) : undefined
          }
          onTooltipViewed={() => {
            EclipsePackagePickerModule.internals.bi.onFeatureTooltipHover?.({
              featureKey: feature.content?.name?.translationKey,
            });
          }}
        />
      </PlatformFeaturesGrid.Content.FeatureRow.Cell>
      {products?.map((product) => (
        <FeaturesGridFeatureRowCell
          key={`${feature.id}-${product.productId}`}
          feature={feature}
          productId={product.productId ?? ''}
        />
      ))}
    </PlatformFeaturesGrid.Content.FeatureRow.Root>
  );
};
